import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { useAuth } from "@/components/Auth/AuthContext";
import { Button, Modal, Textarea } from "@/ui/components";
import { feedbackReportService, type FeedbackReportType } from "@/services/feedbackReportService";
import "./FeedbackReporter.css";

type MarkupTool = "none" | "arrow" | "rectangle";

type ScreenshotAnnotation = {
  id: string;
  type: Exclude<MarkupTool, "none">;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
};

type ScreenshotDraft = {
  blob: Blob;
  base64: string;
  contentType: string;
  fileName: string;
  capturedAt: string;
  source: "auto" | "manual";
};

const SCREENSHOT_TARGET_MAX_BYTES = 320 * 1024;
const SCREENSHOT_MAX_WIDTH = 1600;
const SCREENSHOT_MAX_HEIGHT = 2400;
const SCREENSHOT_MAX_PIXELS = 2_100_000;
const SCREENSHOT_DEFAULT_QUALITY = 0.78;
const SCREENSHOT_MIN_QUALITY = 0.46;
const SCREENSHOT_QUALITY_STEP = 0.08;
const SCREENSHOT_CONTENT_TYPE = "image/jpeg";
const SCREENSHOT_EXTENSION = "jpg";
const MARKUP_COLOR = "#ef4444";
const MARKUP_OUTLINE_COLOR = "rgba(255, 255, 255, 0.96)";
const MIN_ANNOTATION_DISTANCE = 12;

const BOTTOM_NAV_PATHS = new Set([
  "/",
  "/dashboard",
  "/projects",
  "/invoices",
  "/quotations",
  "/clients",
  "/certificates",
  "/tasks",
  "/users",
  "/templates",
]);
const PUBLIC_IFC_VIEWER_PATH_PREFIXES = ["/projects/ifc", "/viewer/ifc"];

function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const commaIndex = result.indexOf(",");
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

function sanitizeFileStem(value: string): string {
  const trimmed = value.trim().toLowerCase();
  const sanitized = trimmed.replace(/[^a-z0-9._-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
  return sanitized || `pmapp-feedback-${Date.now()}`;
}

function buildScreenshotFileName(seed: string): string {
  const withoutExtension = seed.replace(/\.[a-z0-9]+$/i, "");
  return `${sanitizeFileStem(withoutExtension)}.${SCREENSHOT_EXTENSION}`;
}

function clampAnnotationBounds(value: number, max: number): number {
  return Math.max(0, Math.min(max, value));
}

function getAnnotationDistance(annotation: ScreenshotAnnotation): number {
  return Math.hypot(annotation.endX - annotation.startX, annotation.endY - annotation.startY);
}

function isMeaningfulAnnotation(annotation: ScreenshotAnnotation): boolean {
  return getAnnotationDistance(annotation) >= MIN_ANNOTATION_DISTANCE;
}

function createAnnotationId(): string {
  return `feedback-markup-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function getScaledScreenshotDimensions(width: number, height: number): { width: number; height: number } {
  const safeWidth = Math.max(1, Math.round(width));
  const safeHeight = Math.max(1, Math.round(height));
  const dimensionScale = Math.min(
    1,
    SCREENSHOT_MAX_WIDTH / safeWidth,
    SCREENSHOT_MAX_HEIGHT / safeHeight,
  );
  const pixelScale = Math.min(1, Math.sqrt(SCREENSHOT_MAX_PIXELS / (safeWidth * safeHeight)));
  const scale = Math.min(dimensionScale, pixelScale);

  return {
    width: Math.max(1, Math.round(safeWidth * scale)),
    height: Math.max(1, Math.round(safeHeight * scale)),
  };
}

function getMarkupStrokeWidth(width: number, height: number): number {
  const shortestSide = Math.max(1, Math.min(width, height));
  return Math.max(4, Math.min(14, shortestSide * 0.012));
}

function drawArrowPath(
  context: CanvasRenderingContext2D,
  annotation: ScreenshotAnnotation,
  strokeStyle: string,
  lineWidth: number,
) {
  const { startX, startY, endX, endY } = annotation;
  const angle = Math.atan2(endY - startY, endX - startX);
  const arrowHeadLength = Math.max(16, lineWidth * 3.4);
  const leftX = endX - arrowHeadLength * Math.cos(angle - Math.PI / 6);
  const leftY = endY - arrowHeadLength * Math.sin(angle - Math.PI / 6);
  const rightX = endX - arrowHeadLength * Math.cos(angle + Math.PI / 6);
  const rightY = endY - arrowHeadLength * Math.sin(angle + Math.PI / 6);

  context.save();
  context.strokeStyle = strokeStyle;
  context.fillStyle = strokeStyle;
  context.lineWidth = lineWidth;
  context.lineCap = "round";
  context.lineJoin = "round";

  context.beginPath();
  context.moveTo(startX, startY);
  context.lineTo(endX, endY);
  context.stroke();

  context.beginPath();
  context.moveTo(endX, endY);
  context.lineTo(leftX, leftY);
  context.lineTo(rightX, rightY);
  context.closePath();
  context.fill();
  context.restore();
}

function drawRectanglePath(
  context: CanvasRenderingContext2D,
  annotation: ScreenshotAnnotation,
  strokeStyle: string,
  lineWidth: number,
) {
  const x = Math.min(annotation.startX, annotation.endX);
  const y = Math.min(annotation.startY, annotation.endY);
  const width = Math.abs(annotation.endX - annotation.startX);
  const height = Math.abs(annotation.endY - annotation.startY);

  context.save();
  context.strokeStyle = strokeStyle;
  context.lineWidth = lineWidth;
  context.lineCap = "round";
  context.lineJoin = "round";
  context.strokeRect(x, y, width, height);
  context.restore();
}

function drawAnnotation(context: CanvasRenderingContext2D, annotation: ScreenshotAnnotation, width: number, height: number) {
  const strokeWidth = getMarkupStrokeWidth(width, height);
  const outlineWidth = strokeWidth + 4;

  if (annotation.type === "rectangle") {
    drawRectanglePath(context, annotation, MARKUP_OUTLINE_COLOR, outlineWidth);
    drawRectanglePath(context, annotation, MARKUP_COLOR, strokeWidth);
    return;
  }

  drawArrowPath(context, annotation, MARKUP_OUTLINE_COLOR, outlineWidth);
  drawArrowPath(context, annotation, MARKUP_COLOR, strokeWidth);
}

async function buildAnnotatedScreenshotDraft(
  screenshot: ScreenshotDraft,
  annotations: ScreenshotAnnotation[],
): Promise<ScreenshotDraft> {
  if (!annotations.length) {
    return screenshot;
  }

  const image = await loadImageFromBlob(screenshot.blob);
  const width = Math.max(1, image.naturalWidth || image.width || 1);
  const height = Math.max(1, image.naturalHeight || image.height || 1);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) {
    return screenshot;
  }

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);
  for (const annotation of annotations) {
    drawAnnotation(context, annotation, width, height);
  }

  return createScreenshotDraft(await encodeCanvasForUpload(canvas), {
    capturedAt: screenshot.capturedAt,
    fileNameSeed: screenshot.fileName,
    source: screenshot.source,
    skipNormalization: true,
  });
}

function getPreviewPoint(
  event: React.PointerEvent<SVGSVGElement>,
  width: number,
  height: number,
): { x: number; y: number } {
  const rect = event.currentTarget.getBoundingClientRect();
  const relativeX = rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0;
  const relativeY = rect.height > 0 ? (event.clientY - rect.top) / rect.height : 0;
  return {
    x: clampAnnotationBounds(relativeX * width, width),
    y: clampAnnotationBounds(relativeY * height, height),
  };
}

function renderSvgAnnotation(annotation: ScreenshotAnnotation, width: number, height: number) {
  const strokeWidth = getMarkupStrokeWidth(width, height);
  const outlineWidth = strokeWidth + 4;

  if (annotation.type === "rectangle") {
    const x = Math.min(annotation.startX, annotation.endX);
    const y = Math.min(annotation.startY, annotation.endY);
    const rectWidth = Math.abs(annotation.endX - annotation.startX);
    const rectHeight = Math.abs(annotation.endY - annotation.startY);
    return (
      <g key={annotation.id}>
        <rect x={x} y={y} width={rectWidth} height={rectHeight} fill="none" stroke={MARKUP_OUTLINE_COLOR} strokeWidth={outlineWidth} rx={Math.max(6, strokeWidth)} />
        <rect x={x} y={y} width={rectWidth} height={rectHeight} fill="none" stroke={MARKUP_COLOR} strokeWidth={strokeWidth} rx={Math.max(6, strokeWidth)} />
      </g>
    );
  }

  const angle = Math.atan2(annotation.endY - annotation.startY, annotation.endX - annotation.startX);
  const arrowHeadLength = Math.max(16, strokeWidth * 3.4);
  const leftX = annotation.endX - arrowHeadLength * Math.cos(angle - Math.PI / 6);
  const leftY = annotation.endY - arrowHeadLength * Math.sin(angle - Math.PI / 6);
  const rightX = annotation.endX - arrowHeadLength * Math.cos(angle + Math.PI / 6);
  const rightY = annotation.endY - arrowHeadLength * Math.sin(angle + Math.PI / 6);
  const points = `${annotation.endX},${annotation.endY} ${leftX},${leftY} ${rightX},${rightY}`;

  return (
    <g key={annotation.id}>
      <line
        x1={annotation.startX}
        y1={annotation.startY}
        x2={annotation.endX}
        y2={annotation.endY}
        stroke={MARKUP_OUTLINE_COLOR}
        strokeWidth={outlineWidth}
        strokeLinecap="round"
      />
      <polygon points={points} fill={MARKUP_OUTLINE_COLOR} />
      <line
        x1={annotation.startX}
        y1={annotation.startY}
        x2={annotation.endX}
        y2={annotation.endY}
        stroke={MARKUP_COLOR}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />
      <polygon points={points} fill={MARKUP_COLOR} />
    </g>
  );
}

function canvasToBlob(canvas: HTMLCanvasElement | { toBlob: HTMLCanvasElement["toBlob"] }, quality = SCREENSHOT_DEFAULT_QUALITY): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((value) => {
      if (value) {
        resolve(value);
        return;
      }
      reject(new Error("Screenshot capture returned an empty image"));
    }, SCREENSHOT_CONTENT_TYPE, quality);
  });
}

async function loadImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const nextImage = new Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () => reject(new Error("Could not decode the uploaded screenshot."));
      nextImage.src = objectUrl;
    });
    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function encodeCanvasForUpload(
  sourceCanvas: HTMLCanvasElement | { toBlob: HTMLCanvasElement["toBlob"] },
): Promise<Blob> {
  let quality = SCREENSHOT_DEFAULT_QUALITY;
  let blob = await canvasToBlob(sourceCanvas, quality);

  while (blob.size > SCREENSHOT_TARGET_MAX_BYTES && quality > SCREENSHOT_MIN_QUALITY) {
    quality = Math.max(SCREENSHOT_MIN_QUALITY, Number((quality - SCREENSHOT_QUALITY_STEP).toFixed(2)));
    blob = await canvasToBlob(sourceCanvas, quality);
    if (quality === SCREENSHOT_MIN_QUALITY) {
      break;
    }
  }

  return blob;
}

async function normalizeScreenshotBlob(blob: Blob): Promise<Blob> {
  try {
    const image = await loadImageFromBlob(blob);
    const targetSize = getScaledScreenshotDimensions(image.naturalWidth || image.width || 1, image.naturalHeight || image.height || 1);
    const canvas = document.createElement("canvas");
    canvas.width = targetSize.width;
    canvas.height = targetSize.height;
    const context = canvas.getContext("2d");
    if (!context) {
      return blob;
    }
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, targetSize.width, targetSize.height);
    return encodeCanvasForUpload(canvas);
  } catch {
    return blob;
  }
}

async function createScreenshotDraft(
  blob: Blob,
  options: { capturedAt?: string; fileNameSeed: string; source: ScreenshotDraft["source"]; skipNormalization?: boolean },
): Promise<ScreenshotDraft> {
  const normalizedBlob = options.skipNormalization ? blob : await normalizeScreenshotBlob(blob);
  return {
    blob: normalizedBlob,
    base64: await fileToBase64(normalizedBlob),
    contentType: normalizedBlob.type || SCREENSHOT_CONTENT_TYPE,
    fileName: buildScreenshotFileName(options.fileNameSeed),
    capturedAt: options.capturedAt || new Date().toISOString(),
    source: options.source,
  };
}

async function captureCurrentPage(): Promise<ScreenshotDraft> {
  const html2canvas = (await import("html2canvas")).default;
  const capturedAt = new Date().toISOString();
  await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
  const canvas = await html2canvas(document.body, {
    logging: false,
    useCORS: true,
    backgroundColor: "#ffffff",
    scale: 1,
    ignoreElements: (element) => element instanceof HTMLElement && element.dataset.feedbackReporterIgnore === "true",
  });
  const blob = await encodeCanvasForUpload(canvas);
  return createScreenshotDraft(blob, {
    capturedAt,
    fileNameSeed: `pmapp-feedback-${Date.now()}`,
    source: "auto",
    skipNormalization: true,
  });
}

export default function FeedbackReporter() {
  const { pathname } = useLocation();
  const { isAuthenticated, user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reportType, setReportType] = useState<FeedbackReportType>("bug");
  const [description, setDescription] = useState("");
  const [screenshot, setScreenshot] = useState<ScreenshotDraft | null>(null);
  const [captureNotice, setCaptureNotice] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [markupTool, setMarkupTool] = useState<MarkupTool>("none");
  const [annotations, setAnnotations] = useState<ScreenshotAnnotation[]>([]);
  const [draftAnnotation, setDraftAnnotation] = useState<ScreenshotAnnotation | null>(null);
  const [previewDimensions, setPreviewDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  const normalizedPathname = (pathname || "").toLowerCase();
  const hidden =
    !isAuthenticated ||
    normalizedPathname.startsWith("/refer") ||
    normalizedPathname.startsWith("/quotation-signing") ||
    normalizedPathname.startsWith("/login") ||
    normalizedPathname.startsWith("/.auth") ||
    PUBLIC_IFC_VIEWER_PATH_PREFIXES.some((prefix) => normalizedPathname.startsWith(prefix));
  const showBottomNavOffset = BOTTOM_NAV_PATHS.has(normalizedPathname);
  const previewUrl = useMemo(() => (
    screenshot ? URL.createObjectURL(screenshot.blob) : null
  ), [screenshot]);
  const screenshotSizeLabel = useMemo(() => {
    if (!screenshot?.blob) {
      return null;
    }
    const sizeKb = screenshot.blob.size / 1024;
    return sizeKb >= 1024
      ? `${(sizeKb / 1024).toFixed(2)} MB`
      : `${Math.max(1, Math.round(sizeKb))} KB`;
  }, [screenshot]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  if (hidden) {
    return null;
  }

  const resetForm = () => {
    setReportType("bug");
    setDescription("");
    setScreenshot(null);
    setCaptureNotice(null);
    setFormError(null);
    setMarkupTool("none");
    setAnnotations([]);
    setDraftAnnotation(null);
    setPreviewDimensions({ width: 0, height: 0 });
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const handleOpen = async () => {
    setIsPreparing(true);
    resetForm();
    try {
      const captured = await captureCurrentPage();
      setScreenshot(captured);
      setAnnotations([]);
      setDraftAnnotation(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Automatic screenshot capture failed.";
      setCaptureNotice(`${message} You can still upload a screenshot manually.`);
    } finally {
      setOpen(true);
      setIsPreparing(false);
    }
  };

  const handleManualFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const nextScreenshot = await createScreenshotDraft(file, {
        fileNameSeed: file.name || `pmapp-feedback-${Date.now()}`,
        source: "manual",
      });
      setScreenshot(nextScreenshot);
      setCaptureNotice(null);
      setAnnotations([]);
      setDraftAnnotation(null);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "Could not read the uploaded screenshot.");
    } finally {
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  const handleSubmit = async () => {
    if (description.trim().length < 3) {
      setFormError("Add a short description before sending the report.");
      return;
    }

    setIsSubmitting(true);
    setFormError(null);
    try {
      const screenshotForSubmit = screenshot
        ? await buildAnnotatedScreenshotDraft(screenshot, annotations)
        : null;
      const result = await feedbackReportService.submitReport({
        reportType,
        description: description.trim(),
        routePath: pathname || "/",
        pageTitle: document.title || null,
        capturedAt: screenshotForSubmit?.capturedAt || null,
        screenshotBase64: screenshotForSubmit?.base64 || null,
        screenshotContentType: screenshotForSubmit?.contentType || null,
        screenshotFileName: screenshotForSubmit?.fileName || null,
        clientContext: {
          userAgent: navigator.userAgent,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
          pageUrl: window.location.href,
        },
      });

      notifications.show({
        color: result.deduplicated ? "yellow" : "green",
        title: result.deduplicated ? "Already reported today" : "Report sent",
        message: result.deduplicated
          ? `Matched existing report ${result.reportId}. We tracked this as a duplicate so it won’t be handled twice.`
          : `${reportType === "bug" ? "Bug report" : "Suggestion"} ${result.reportId} was saved successfully.`,
      });
      handleClose();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "Could not submit the feedback report.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkupPointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!screenshot || markupTool === "none" || !previewDimensions.width || !previewDimensions.height) {
      return;
    }
    const point = getPreviewPoint(event, previewDimensions.width, previewDimensions.height);
    const nextAnnotation: ScreenshotAnnotation = {
      id: createAnnotationId(),
      type: markupTool,
      startX: point.x,
      startY: point.y,
      endX: point.x,
      endY: point.y,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDraftAnnotation(nextAnnotation);
  };

  const handleMarkupPointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!draftAnnotation || !previewDimensions.width || !previewDimensions.height) {
      return;
    }
    const point = getPreviewPoint(event, previewDimensions.width, previewDimensions.height);
    setDraftAnnotation({
      ...draftAnnotation,
      endX: point.x,
      endY: point.y,
    });
  };

  const finishDraftAnnotation = () => {
    if (!draftAnnotation) {
      return;
    }
    if (isMeaningfulAnnotation(draftAnnotation)) {
      setAnnotations((current) => [...current, draftAnnotation]);
    }
    setDraftAnnotation(null);
  };

  return (
    <>
      <button
        type="button"
        className="feedback-reporter__fab"
        style={{ bottom: showBottomNavOffset ? "calc(96px + env(safe-area-inset-bottom))" : "24px" }}
        aria-label="Report bug or suggestion"
        data-feedback-reporter-ignore="true"
        onClick={handleOpen}
        disabled={isPreparing}
      >
        <span className="feedback-reporter__fab-plus">{isPreparing ? "…" : "+"}</span>
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="feedback-reporter__file-input"
        onChange={handleManualFileChange}
      />

      <Modal
        open={open}
        onClose={handleClose}
        title="Report bug or suggestion"
        size="lg"
        footer={(
          <div className="feedback-reporter__footer">
            <Button type="button" variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit} loading={isSubmitting}>
              Send report
            </Button>
          </div>
        )}
      >
        <div className="feedback-reporter" data-feedback-reporter-ignore="true">
          <div className="feedback-reporter__type-row">
            <button
              type="button"
              className={`feedback-reporter__type-pill${reportType === "bug" ? " is-active" : ""}`}
              onClick={() => setReportType("bug")}
            >
              Bug
            </button>
            <button
              type="button"
              className={`feedback-reporter__type-pill${reportType === "suggestion" ? " is-active" : ""}`}
              onClick={() => setReportType("suggestion")}
            >
              Suggestion
            </button>
          </div>

          <div className="feedback-reporter__meta">
            <span>Route: {pathname || "/"}</span>
            <span>User: {String(user?.userDetails || "").trim().toLowerCase() || "Unknown"}</span>
          </div>

          <Textarea
            label={reportType === "bug" ? "What went wrong?" : "What should improve?"}
            placeholder={reportType === "bug" ? "Describe the issue, what you expected, and what happened instead." : "Describe the improvement idea and where it would help."}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={5}
            fullWidth
          />

          <div className="feedback-reporter__screenshot-card">
            <div className="feedback-reporter__screenshot-header">
              <div>
                <strong>Screenshot</strong>
                <div className="feedback-reporter__screenshot-subtitle">
                  Auto-captured from the current page, downscaled and compressed for faster upload and review. Add a quick arrow or rectangle if you want to point directly at the bug.
                </div>
              </div>
              <div className="feedback-reporter__screenshot-actions">
                <Button type="button" variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
                  Replace
                </Button>
                {screenshot ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setScreenshot(null);
                      setAnnotations([]);
                      setDraftAnnotation(null);
                      setMarkupTool("none");
                    }}
                  >
                    Remove
                  </Button>
                ) : null}
              </div>
            </div>

            {previewUrl ? (
              <>
                <div className="feedback-reporter__markup-toolbar">
                  <button
                    type="button"
                    className={`feedback-reporter__markup-pill${markupTool === "arrow" ? " is-active" : ""}`}
                    onClick={() => setMarkupTool((current) => current === "arrow" ? "none" : "arrow")}
                  >
                    Arrow
                  </button>
                  <button
                    type="button"
                    className={`feedback-reporter__markup-pill${markupTool === "rectangle" ? " is-active" : ""}`}
                    onClick={() => setMarkupTool((current) => current === "rectangle" ? "none" : "rectangle")}
                  >
                    Rectangle
                  </button>
                  <button
                    type="button"
                    className="feedback-reporter__markup-pill"
                    onClick={() => {
                      setAnnotations([]);
                      setDraftAnnotation(null);
                    }}
                    disabled={!annotations.length && !draftAnnotation}
                  >
                    Clear marks
                  </button>
                  {screenshotSizeLabel ? (
                    <span className="feedback-reporter__screenshot-size">Upload: {screenshotSizeLabel}</span>
                  ) : null}
                </div>

                <div className="feedback-reporter__markup-hint">
                  {markupTool === "none"
                    ? "Choose Arrow or Rectangle, then drag on the screenshot to highlight the issue."
                    : `Drawing ${markupTool}. Drag on the screenshot to place it.`}
                </div>

                <div className="feedback-reporter__preview-frame">
                  <img
                    className="feedback-reporter__preview"
                    src={previewUrl}
                    alt="Feedback screenshot preview"
                    onLoad={(event) => {
                      setPreviewDimensions({
                        width: Math.max(1, event.currentTarget.naturalWidth || 1),
                        height: Math.max(1, event.currentTarget.naturalHeight || 1),
                      });
                    }}
                  />
                  {previewDimensions.width > 0 && previewDimensions.height > 0 ? (
                    <svg
                      className={`feedback-reporter__markup-layer${markupTool !== "none" ? " is-drawing" : ""}`}
                      viewBox={`0 0 ${previewDimensions.width} ${previewDimensions.height}`}
                      preserveAspectRatio="none"
                      onPointerDown={handleMarkupPointerDown}
                      onPointerMove={handleMarkupPointerMove}
                      onPointerUp={finishDraftAnnotation}
                      onPointerCancel={finishDraftAnnotation}
                    >
                      {annotations.map((annotation) => renderSvgAnnotation(annotation, previewDimensions.width, previewDimensions.height))}
                      {draftAnnotation ? renderSvgAnnotation(draftAnnotation, previewDimensions.width, previewDimensions.height) : null}
                    </svg>
                  ) : null}
                </div>
              </>
            ) : (
              <div className="feedback-reporter__empty-preview">
                No screenshot attached yet.
              </div>
            )}

            {captureNotice ? (
              <div className="feedback-reporter__notice">{captureNotice}</div>
            ) : null}
            {formError ? (
              <div className="feedback-reporter__error">{formError}</div>
            ) : null}
          </div>
        </div>
      </Modal>
    </>
  );
}
